/**
 * Bulk-edit data model — the rows the builder accumulates, and the rules for
 * what makes a set of them coherent.
 *
 * Bulk edit is not mass edit. Mass edit holds a *per-recording* desired value
 * and diffs it against that recording's current state (see `massEditModel.ts`);
 * bulk edit holds **one plan** — a list of field/operation/value rows — that is
 * applied verbatim to every selected recording. So there is nothing to diff
 * here, but there *is* something to check: rows can contradict each other, and
 * the contract's buckets can only hold one coherent instruction each.
 *
 * Kept free of React/JSX so both the modal (`Components/BulkEditModal.tsx`) and
 * the apply layer (`bulkEditBatch.ts`) can import it without a cycle, and so
 * the rules stay unit-testable without a DOM.
 */

import type { ContributionSavePayload } from './Components/ContributionDetailModal';
import type { ArtistEntry } from './Components/ArtistEditorModal';
import type { ReleaseSelection } from './Components/ChangeReleaseModal';

export type BulkEditField =
  | 'Recording Title'
  | 'Artist'
  | 'Live'
  | 'Contribution'
  | 'Linked Release'
  | 'Year of Recording'
  | 'Country of Recording'
  | '(P) Name';

export type BulkEditOperation =
  | 'Add'
  | 'Edit'
  | 'Delete'
  | 'Demote'
  | 'Replace'
  | 'Change link to Release';

export const FIELD_OPERATIONS: Record<BulkEditField, BulkEditOperation[]> = {
  'Recording Title': ['Edit'],
  // "Demote" moves matching artists to non-featured (mirrors the edit form's
  // demote-vs-remove); "Delete" removes them outright.
  Artist: ['Add', 'Demote', 'Delete', 'Replace'],
  Live: ['Edit'],
  // For Artist & Contribution, "Replace" means replace all currently set with
  // the provided values. "Edit" changes the detail (data quality, evidence,
  // alias) of contributions already on the recordings without adding any.
  Contribution: ['Add', 'Edit', 'Delete', 'Replace'],
  'Linked Release': ['Change link to Release'],
  'Year of Recording': ['Edit'],
  'Country of Recording': ['Edit'],
  // (P) Name is a list on the recording: Add appends a name, Delete removes a
  // matching one. There is no Edit — a rename is a Delete plus an Add.
  '(P) Name': ['Add', 'Delete'],
};

export const FIELDS = Object.keys(FIELD_OPERATIONS) as BulkEditField[];

/** A contribution the user has configured (role + detail payload). */
export interface ConfiguredContribution extends ContributionSavePayload {}

/** The value captured for a row — shape depends on field/operation. */
export type RowValue =
  | { kind: 'title'; title: string }
  | { kind: 'live'; descriptor: string }
  | { kind: 'contributions'; entries: ConfiguredContribution[] }
  | { kind: 'contributionDelete'; roles: string[] }
  | { kind: 'artist'; entries: ArtistEntry[] }
  | { kind: 'artistDelete'; artists: string[] }
  | { kind: 'artistDemote'; artists: string[] }
  /** Four-digit year (`yearRecorded` on the wire). */
  | { kind: 'yearRecorded'; year: string }
  /** ISO 3166-1 alpha-3 code (`countryOfRecording` on the wire; the grid's
   *  `location` column). */
  | { kind: 'country'; code: string }
  /** (P) names to add to, or remove from, each recording's `pNames`. One
   *  row carries the whole list; the builder emits one op per name. */
  | { kind: 'pName'; names: string[] }
  | {
      kind: 'release';
      /** Human-readable release label for the row summary. */
      label: string;
      /** The linkage itself: either an existing release id, or the metadata to
       *  create one from. Absent only for rows built before this field existed
       *  — an apply without it is rejected rather than guessed at. */
      selection?: ReleaseSelection;
      /** Display-only echoes of the chosen release, used by the row summary. */
      release?: string;
      earliestParentReleaseId?: string;
      sourceReleaseId?: string[];
      year?: string | null;
      format?: string;
      mediaType?: string;
    };

export interface BulkEditRow {
  id: string;
  field: BulkEditField | '';
  operation: BulkEditOperation | '';
  value: RowValue | null;
}

/** Which secondary editor a field/operation combination uses. */
export type EditorKind = RowValue['kind'];

export function editorKindFor(
  field: BulkEditField,
  operation: BulkEditOperation
): EditorKind {
  if (field === 'Recording Title') return 'title';
  if (field === 'Live') return 'live';
  if (field === 'Linked Release') return 'release';
  if (field === 'Year of Recording') return 'yearRecorded';
  if (field === 'Country of Recording') return 'country';
  if (field === '(P) Name') return 'pName';
  if (field === 'Artist') {
    if (operation === 'Delete') return 'artistDelete';
    if (operation === 'Demote') return 'artistDemote';
    return 'artist';
  }
  // Contribution: Delete → checklist; Add / Edit / Replace → list builder.
  if (operation === 'Delete') return 'contributionDelete';
  return 'contributions';
}

/** True when a row carries everything needed to be sent. */
export function rowIsComplete(row: BulkEditRow): boolean {
  if (!row.field || !row.operation || !row.value) return false;
  switch (row.value.kind) {
    case 'title':
      return row.value.title.trim().length > 0;
    case 'live':
      // A live descriptor is required (mirrors the edit form / mass edit).
      return row.value.descriptor.trim().length > 0;
    case 'contributions':
      return row.value.entries.length > 0;
    case 'contributionDelete':
      return row.value.roles.length > 0;
    case 'artist':
      return row.value.entries.length > 0;
    case 'artistDelete':
      return row.value.artists.length > 0;
    case 'artistDemote':
      return row.value.artists.length > 0;
    case 'yearRecorded':
      // Bulk edit sets a year; it never clears one (an empty value would be an
      // "unset" instruction the builder has no operation for).
      return /^\d{4}$/.test(row.value.year.trim());
    case 'country':
      return row.value.code.trim().length > 0;
    case 'pName':
      return row.value.names.some((n) => n.trim().length > 0);
    case 'release':
      return row.value.label.trim().length > 0 && !!row.value.selection;
    default:
      return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Coherence rules                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Per field, the operations that *define what the value becomes*.
 *
 * At most one of these may appear in a plan, because they are competing answers
 * to the same question. "Add these contributions" and "Replace all
 * contributions with these" both write the single `contributions` bucket, which
 * the backend applies in array order — so whichever the user meant, one of them
 * silently wins. The operations deliberately left out (Delete, Demote) only
 * take things away, so one of those can ride alongside a set-defining row
 * without ambiguity: that is the "one, or two if deleting" shape.
 */
const SET_OPERATIONS: Record<BulkEditField, BulkEditOperation[]> = {
  'Recording Title': ['Edit'],
  Artist: ['Add', 'Replace'],
  Live: ['Edit'],
  Contribution: ['Add', 'Edit', 'Replace'],
  'Linked Release': ['Change link to Release'],
  'Year of Recording': ['Edit'],
  'Country of Recording': ['Edit'],
  // Add defines what the list gains; Delete only takes away, so the two can
  // ride together (add one name, drop another) like Contribution's Add + Delete.
  '(P) Name': ['Add'],
};

/**
 * Per field, the operations that must stand alone.
 *
 * "Replace" does not contribute to the value — it *is* the value: the bucket it
 * sends supersedes whatever the recordings currently hold. Pairing it with a
 * Delete row is therefore meaningless (the deleted entries are gone either way,
 * and if the delete lands after the replace it eats part of the replacement),
 * and pairing it with Add or Edit is a straight contradiction. Add and Edit
 * leave the untouched entries alone, which is why those two *can* take a Delete
 * alongside them.
 */
const EXCLUSIVE_OPERATIONS: Partial<
  Record<BulkEditField, BulkEditOperation[]>
> = {
  Artist: ['Replace'],
  Contribution: ['Replace'],
};

/** Wording for fields with a single operation, where "one row per operation"
 *  and "one row per field" are the same rule and the generic phrasing reads
 *  like boilerplate. */
const SINGLE_ROW_MESSAGE: Partial<Record<BulkEditField, string>> = {
  'Recording Title':
    'Only one recording title can be applied — remove the extra title row.',
  Live: 'Only one live descriptor can be applied — remove the extra live row.',
  'Linked Release':
    'Only one release can be linked per bulk edit — remove the extra release row.',
  'Year of Recording':
    'Only one year of recording can be applied — remove the extra year row.',
  'Country of Recording':
    'Only one country of recording can be applied — remove the extra country row.',
};

/** Wording for fields where several operations compete to define the value. */
const COMPETING_OPS_MESSAGE: Partial<Record<BulkEditField, string>> = {
  Contribution:
    'Contributions can be added, edited or replaced — but only one of those per bulk edit. Add and Edit can each be combined with a Delete row.',
  Artist:
    'Artists can be added or replaced — but only one of those per bulk edit. Add can be combined with a Delete or Demote row.',
};

/** Wording for the operations that must stand alone. */
const EXCLUSIVE_MESSAGE: Partial<Record<BulkEditField, string>> = {
  Contribution:
    'Replace rewrites the whole contribution set, so it cannot be combined with any other Contribution row.',
  Artist:
    'Replace rewrites the whole artist list, so it cannot be combined with any other Artist row.',
};

/**
 * The operations still selectable for `field`, given what the other rows have
 * already claimed.
 *
 * This is the same rule pair `bulkEditConflicts` enforces, applied one step
 * earlier: a row claims its operation the moment it is chosen, before any value
 * is set. Letting the user build three Contribution rows and only then telling
 * them two must go wastes the work, so the builder greys the impossible choices
 * out instead. Conflicts remain the backstop — rows can be re-pointed at a
 * different field after the fact.
 *
 * `exceptRowId` is the row being edited: its own claim must not rule out its
 * own current selection.
 */
export function availableOperations(
  field: BulkEditField,
  rows: BulkEditRow[],
  exceptRowId?: string
): BulkEditOperation[] {
  const claimed = rows
    .filter((r) => r.id !== exceptRowId && r.field === field && r.operation)
    .map((r) => r.operation as BulkEditOperation);
  const exclusiveOps = EXCLUSIVE_OPERATIONS[field] ?? [];
  // A stand-alone operation is already there: nothing else may join it.
  if (claimed.some((op) => exclusiveOps.includes(op))) return [];

  const setOps = SET_OPERATIONS[field];
  const setOpClaimed = claimed.some((op) => setOps.includes(op));
  return FIELD_OPERATIONS[field].filter(
    (op) =>
      !claimed.includes(op) &&
      !(setOpClaimed && setOps.includes(op)) &&
      // …and it may not join anything either.
      !(claimed.length > 0 && exclusiveOps.includes(op))
  );
}

/** True when every operation on `field` is already claimed by another row, so
 *  offering the field again could only produce a conflict. */
export function fieldIsExhausted(
  field: BulkEditField,
  rows: BulkEditRow[],
  exceptRowId?: string
): boolean {
  return availableOperations(field, rows, exceptRowId).length === 0;
}

/**
 * Reasons this set of rows can't be sent as one plan.
 *
 * All of these are contradictions *within the plan*, which the contract has no
 * way to express and DS has no way to resolve: one request carries one
 * `correct` list, one `contributions` list, one `change_artists` bucket and at
 * most one `release_link` (contract §3.2, §3.4.4). Two rows that disagree about
 * the same thing would arrive as two instructions applied in array order, so
 * whichever the user meant, one of them silently loses. Caught here instead.
 *
 * Returns one message per problem, empty when the plan is coherent.
 */
export function bulkEditConflicts(rows: BulkEditRow[]): string[] {
  const ready = rows.filter(rowIsComplete);
  const problems: string[] = [];

  for (const field of FIELDS) {
    const ops = ready
      .filter((r) => r.field === field)
      .map((r) => r.operation as BulkEditOperation);
    if (ops.length < 2) continue;

    // One row per operation. Two "Add contribution" rows are one row's worth of
    // values split in two, and only one of them would reach the bucket.
    const seen = new Set<BulkEditOperation>();
    const duplicated = new Set<BulkEditOperation>();
    ops.forEach((op) => (seen.has(op) ? duplicated.add(op) : seen.add(op)));
    duplicated.forEach((op) =>
      problems.push(
        SINGLE_ROW_MESSAGE[field] ??
          `Only one “${op}” row is allowed for ${field} — put all of its values in a single row.`
      )
    );

    // A stand-alone operation rules out everything else, and says so in its own
    // words — reporting it *and* the competing-operations problem below would
    // be two messages about one mistake.
    const exclusive = Array.from(seen).filter((op) =>
      (EXCLUSIVE_OPERATIONS[field] ?? []).includes(op)
    );
    if (exclusive.length > 0 && seen.size > 1) {
      problems.push(
        EXCLUSIVE_MESSAGE[field] ??
          `“${exclusive[0]}” rewrites ${field} entirely, so it cannot be combined with another ${field} row.`
      );
      continue;
    }

    // …and only one of the operations that define what the value becomes.
    const competing = Array.from(seen).filter((op) =>
      SET_OPERATIONS[field].includes(op)
    );
    if (competing.length > 1) {
      problems.push(
        COMPETING_OPS_MESSAGE[field] ??
          `${field} can take only one of ${competing.join(
            ', '
          )} per bulk edit — each defines what the value becomes.`
      );
    }
  }

  // A role both written and deleted in one plan, or the same artist deleted and
  // demoted — the buckets are applied in array order, so one would win silently.
  const lower = (s: string) => s.trim().toLowerCase();
  const writtenRoles = new Set(
    ready
      .filter((r) => r.value?.kind === 'contributions')
      .flatMap((r) =>
        (r.value as Extract<RowValue, { kind: 'contributions' }>).entries.map(
          (e) => lower(e.contribution)
        )
      )
  );
  const deletedRoles = ready
    .filter((r) => r.value?.kind === 'contributionDelete')
    .flatMap((r) =>
      (r.value as Extract<RowValue, { kind: 'contributionDelete' }>).roles.map(
        lower
      )
    );
  const roleClash = deletedRoles.filter((role) => writtenRoles.has(role));
  if (roleClash.length > 0) {
    problems.push(
      `Contribution${
        roleClash.length === 1 ? '' : 's'
      } both set and deleted: ${Array.from(new Set(roleClash)).join(', ')}.`
    );
  }

  const demoted = ready
    .filter((r) => r.value?.kind === 'artistDemote')
    .flatMap((r) =>
      (r.value as Extract<RowValue, { kind: 'artistDemote' }>).artists.map(
        lower
      )
    );
  const deletedArtists = ready
    .filter((r) => r.value?.kind === 'artistDelete')
    .flatMap((r) =>
      (r.value as Extract<RowValue, { kind: 'artistDelete' }>).artists.map(
        lower
      )
    );
  const pNamesOf = (operation: BulkEditOperation): string[] =>
    ready
      .filter((r) => r.value?.kind === 'pName' && r.operation === operation)
      .flatMap((r) =>
        (r.value as Extract<RowValue, { kind: 'pName' }>).names.map(lower)
      );
  const addedPNames = new Set(pNamesOf('Add'));
  const deletedPNames = pNamesOf('Delete');
  const pNameClash = deletedPNames.filter((n) => addedPNames.has(n));
  if (pNameClash.length > 0) {
    problems.push(
      `(P) Name${
        pNameClash.length === 1 ? '' : 's'
      } both added and deleted: ${Array.from(new Set(pNameClash)).join(', ')}.`
    );
  }

  const artistClash = deletedArtists.filter((a) => demoted.includes(a));
  if (artistClash.length > 0) {
    problems.push(
      `Artist${
        artistClash.length === 1 ? '' : 's'
      } both demoted and deleted: ${Array.from(new Set(artistClash)).join(
        ', '
      )}.`
    );
  }

  return problems;
}
